<template>
  <div class="crisis-report-grid" :style="gridStyle">
    <revo-grid
      ref="grid"
      class="crisis-report-revogrid"
      :source.prop="source"
      :columns.prop="columns"
      :readonly.prop="true"
      :resize.prop="false"
      :range.prop="false"
      :filter.prop="false"
      :sorting.prop="undefined"
      :use-clipboard.prop="false"
      :can-focus.prop="false"
      :can-drag.prop="false"
      hide-attribution
      :frame-size.prop="1"
      :row-size.prop="effectiveRowSize"
      :col-size.prop="colSize"
      row-class="__rowClass"
      theme="default"
    />
  </div>
</template>

<script>
import Vue from 'vue';
import { defineCustomElements } from '@revolist/revogrid/loader';

let isRevoGridDefined = false;

function ensureRevoGrid() {
  if (!Vue.config.ignoredElements.includes('revo-grid')) {
    Vue.config.ignoredElements = [...Vue.config.ignoredElements, 'revo-grid'];
  }

  if (!isRevoGridDefined) {
    defineCustomElements();
    isRevoGridDefined = true;
  }
}

export default {
  name: 'CrisisReportGrid',
  props: {
    source: {
      type: Array,
      default: () => []
    },
    columns: {
      type: Array,
      default: () => []
    },
    height: {
      type: [Number, String],
      default: '100%'
    },
    rowSize: {
      type: Number,
      default: 56
    },
    colSize: {
      type: Number,
      default: 160
    },
    rowLabelLines: {
      type: Number,
      default: 2
    },
    headerLines: {
      type: Number,
      default: 2
    },
    cellLines: {
      type: Number,
      default: 2
    },
    headerRowHeight: {
      type: Number,
      default: 44
    },
    headerGroupRows: {
      type: Number,
      default: 1
    }
  },
  computed: {
    normalizedHeight() {
      return typeof this.height === 'number' ? `${this.height}px` : this.height;
    },
    effectiveRowSize() {
      const cellHeight = this.cellLines * 16 + 20;
      const rowLabelHeight = this.rowLabelLines * 16 + 20;

      return Math.max(this.rowSize, cellHeight, rowLabelHeight);
    },
    gridStyle() {
      return {
        height: this.normalizedHeight,
        '--crisis-report-row-label-lines': this.rowLabelLines,
        '--crisis-report-header-lines': this.headerLines,
        '--crisis-report-cell-lines': this.cellLines,
        '--crisis-report-header-row-height': `${this.headerRowHeight}px`,
        '--crisis-report-header-group-height': `${this.headerGroupRows * this.headerRowHeight}px`
      };
    }
  },
  created() {
    ensureRevoGrid();
  },
  watch: {
    source: 'refreshGrid',
    columns: 'refreshGrid',
    rowSize: 'refreshGrid',
    rowLabelLines: 'refreshGrid',
    headerLines: 'refreshGrid',
    cellLines: 'refreshGrid',
    headerRowHeight: 'refreshGrid',
    headerGroupRows: 'refreshGrid'
  },
  methods: {
    refreshGrid() {
      this.$nextTick(() => {
        if (this.$refs.grid && this.$refs.grid.refresh) {
          this.$refs.grid.refresh();
        }
      });
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/variables/_variables.scss";

.crisis-report-grid {
  width: 100%;
  height: 100%;
  min-height: 420px;
  background-color: #fff;
  overflow: hidden;
}

.crisis-report-revogrid {
  width: 100%;
  height: 100%;
  --revo-grid-header-bg: #F2F6FE;
  --revo-grid-header-color: #2D609B;
  --revo-grid-header-border: #d6d9dc;
  --revo-grid-cell-border: #d6d9dc;
  --revo-grid-background: #fff;
  --revo-grid-text: #5e5873;
  --revo-grid-row-hover: #f8fbff;
  font-size: 13px;
  user-select: text !important;
}

.crisis-report-revogrid::v-deep .rgCell,
.crisis-report-revogrid::v-deep .rgHeaderCell {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  border-right: 1px solid #d6d9dc;
  box-sizing: border-box;
}

.crisis-report-revogrid::v-deep .rgHeaderCell {
  color: #2D609B;
  font-weight: 600;
  background: #F2F6FE;
}

.crisis-report-revogrid::v-deep revogr-header {
  line-height: normal !important;
}

.crisis-report-revogrid::v-deep .header-rgRow {
  height: var(--crisis-report-header-row-height) !important;
  min-height: var(--crisis-report-header-row-height) !important;
}

.crisis-report-revogrid::v-deep .group-rgRow {
  height: var(--crisis-report-header-group-height) !important;
  min-height: var(--crisis-report-header-group-height) !important;
  overflow: hidden;
}

.crisis-report-revogrid::v-deep .header-content {
  width: 100%;
  height: 100%;
  min-height: var(--crisis-report-header-row-height);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 0 8px;
  color: inherit;
  line-height: 1.2;
  text-align: center;
  text-overflow: clip !important;
  white-space: normal !important;
}

.crisis-report-revogrid::v-deep .crisis-report-header-text {
  display: -webkit-box;
  max-width: 100%;
  overflow: hidden;
  text-overflow: clip;
  white-space: normal;
  overflow-wrap: anywhere;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: var(--crisis-report-header-lines);
}

.crisis-report-revogrid::v-deep .group-rgRow .rgHeaderCell {
  min-height: 32px;
}

.crisis-report-revogrid::v-deep .crisis-grid-row-header {
  justify-content: flex-start;
  padding-left: 12px;
  font-weight: 600;
  background: #fff;
}

.crisis-report-revogrid::v-deep .crisis-grid-row-header .crisis-report-grid-value {
  display: -webkit-box;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: clip;
  white-space: normal;
  overflow-wrap: anywhere;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: var(--crisis-report-row-label-lines);
}

.crisis-report-revogrid::v-deep .crisis-grid-total-cell,
.crisis-report-revogrid::v-deep .crisis-grid-total-header {
  background: #FEF5DA !important;
  color: #303030;
}

.crisis-report-revogrid::v-deep .crisis-grid-summary-cell,
.crisis-report-revogrid::v-deep .crisis-grid-summary-header {
  background: #dcecd9 !important;
  color: #303030;
}

.crisis-report-revogrid::v-deep .crisis-report-grid-value {
  width: 100%;
  display: -webkit-box;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: clip;
  white-space: normal;
  overflow-wrap: anywhere;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: var(--crisis-report-cell-lines);
}

.dark-layout {
  .crisis-report-grid {
    background-color: var(--main-dark);
  }

  .crisis-report-revogrid {
    --revo-grid-header-bg: #{$theme-dark-table-header-bg};
    --revo-grid-header-color: #d0d2d6;
    --revo-grid-cell-border: #3b4253;
    --revo-grid-header-border: #3b4253;
    --revo-grid-background: var(--main-dark);
    --revo-grid-text: #d0d2d6;
  }

  .crisis-report-revogrid::v-deep .rgHeaderCell {
    color: #d0d2d6;
    background-color: $theme-dark-table-header-bg;
  }

  .crisis-report-revogrid::v-deep .crisis-grid-row-header {
    color: #fff;
    background-color: var(--main-dark);
  }

  .crisis-report-revogrid::v-deep .crisis-grid-total-cell,
  .crisis-report-revogrid::v-deep .crisis-grid-total-header,
  .crisis-report-revogrid::v-deep .crisis-grid-summary-cell,
  .crisis-report-revogrid::v-deep .crisis-grid-summary-header {
    color: #000;
  }
}
</style>
